"use client";

import {
  createDashboardView,
  deleteDashboardView,
  getDashboardViewList,
  updateDashboardView,
  type DashboardViewListResponse,
  type CreateDashboardViewInput,
  DashboardView,
  SortingState,
} from "@/services/dashboard-view.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "./use-analytics";
import { Filter } from "./use-filter";
import { useCallback, useMemo, useRef } from "react";
import { useEntitySchemaByName } from "./schema/use-entity-schema-by-name";
import { useMyAros } from "./schema/use-my-aros";
import { useHasRootPriviliges } from "./auth/use-has-root-priviliges";
import { parseAsString, useQueryState } from "nuqs";

const defaultSorting: SortingState[] = [
  {
    field: "created_at",
    order: "desc",
  },
];

const colsToHideByDefault = ["id", "integer_id"];

const DEFAULT_VIEW_PREFIX = "all-";

export const isDefaultView = (viewId: string | null | undefined): boolean =>
  !!viewId?.startsWith(DEFAULT_VIEW_PREFIX);

export const getDefaultViewId = (entityId: string): string => `${DEFAULT_VIEW_PREFIX}${entityId}`;

export const useDashboardViews = ({
  entityId,
  entityName,
}: {
  entityId: string;
  entityName: string;
}) => {
  const { data: myAros } = useMyAros();
  const { hasRootPriviliges: iMayApplyTemplates } = useHasRootPriviliges();
  const { capture } = useAnalytics();
  const queryClient = useQueryClient();
  const { entitySchemaWithRelations } = useEntitySchemaByName(entityName);
  const [nuqsActiveViewId, setNuqsActiveViewId] = useQueryState("activeView", parseAsString);
  const activeViewIdRef = useRef<string | null>(nuqsActiveViewId);

  // nuqs may briefly return null during route transitions (the URL changes before
  // the new page renders, stripping query params). The ref caches the last intentionally
  // set value so we don't lose track of the active view during that gap.
  // See: https://github.com/47ng/nuqs/discussions/893
  if (nuqsActiveViewId !== null) {
    activeViewIdRef.current = nuqsActiveViewId;
  }

  const setActiveViewId = useCallback(
    (viewId: string | null) => {
      activeViewIdRef.current = viewId;
      setNuqsActiveViewId(viewId);
    },
    [setNuqsActiveViewId],
  );

  // Middleware guarantees auth
  const { data: dashboardViews, isLoading: isLoadingDashboardViews } =
    useQuery<DashboardViewListResponse>({
      queryKey: ["dashboard-views", entityId],
      queryFn: () => getDashboardViewList(entityId),
      enabled: !!entityId,
    });

  const { mutate: createMutation, mutateAsync: createDashboardViewAsync } = useMutation({
    mutationFn: (
      params: Omit<CreateDashboardViewInput, "entity"> & {
        filter?: Array<Filter>;
      },
    ) =>
      createDashboardView({
        ...params,
        entity: entityId ? { id: entityId } : undefined,
      }),
    onSuccess: () => {
      capture("create_dashboard_view");
      queryClient.invalidateQueries({
        queryKey: ["dashboard-views", entityId],
      });
    },
  });

  const { mutate: updateMutation } = useMutation({
    mutationFn: updateDashboardView,
    onSuccess: () => {
      capture("update_dashboard_view");
      queryClient.invalidateQueries({
        queryKey: ["dashboard-views", entityId],
      });
    },
  });

  const { mutate: deleteMutation } = useMutation({
    mutationFn: deleteDashboardView,
    onSuccess: () => {
      capture("delete_dashboard_view");
      queryClient.invalidateQueries({
        queryKey: ["dashboard-views", entityId],
      });
    },
  });

  const defaultView: DashboardView | undefined = useMemo(() => {
    if (
      !entitySchemaWithRelations?.fields ||
      entitySchemaWithRelations.fields.length === 0 ||
      !entityId
    )
      return undefined;

    const allColumns = entitySchemaWithRelations.fields
      //currently hide variant fields from table
      .filter((x) => x.type !== "FieldVariantsData");

    const idColumn = allColumns.find((col) => col.name === "id");
    const integerIdColumn = allColumns.find((col) => col.name === "integer_id");
    const updatedAtColumn = allColumns.find((col) => col.name === "updated_at");
    const createdAtColumn = allColumns.find((col) => col.name === "created_at");

    const otherColumns = allColumns.filter(
      (col) =>
        col.name !== "integer_id" &&
        col.name !== "updated_at" &&
        col.name !== "created_at" &&
        col.name !== "id",
    );

    return {
      id: getDefaultViewId(entityId),
      name: "All fields",
      description: "Default view with all columns",
      entity: entityId ? { id: entityId } : undefined,
      filter: [],
      columnState: {
        columns: [
          ...(idColumn ? [idColumn] : []),
          ...(integerIdColumn ? [integerIdColumn] : []),
          ...otherColumns,
          ...(updatedAtColumn ? [updatedAtColumn] : []),
          ...(createdAtColumn ? [createdAtColumn] : []),
        ].map((col) => ({
          id: col.name,
          size: 300,
          visible: !colsToHideByDefault.includes(col.name),
        })),
        sorting: defaultSorting,
      },
    };
  }, [entityId, entitySchemaWithRelations?.fields]);

  const calculatedDashboardViews = useMemo(() => {
    const filteredViews =
      dashboardViews?.data?.filter((v) => {
        if (iMayApplyTemplates) {
          return true;
        }
        // If no AROs are connected to the view, everyone can see it
        if (!v.pylo_aros || v.pylo_aros.length === 0) {
          return true;
        }
        // Otherwise, check if user has any of the connected AROs
        return v.pylo_aros?.some((aro) => myAros?.data?.some((myAro) => myAro.id === aro.id));
      }) ?? [];

    // Sort by index asc (undefined last, then by name)
    filteredViews.sort((a, b) => {
      const ia = a.index ?? Number.POSITIVE_INFINITY;
      const ib = b.index ?? Number.POSITIVE_INFINITY;
      if (ia !== ib) return ia - ib;
      return (a.name || "").localeCompare(b.name || "");
    });

    const combined = [...filteredViews];
    if (defaultView) {
      combined.push(defaultView);
    }

    return combined.filter((v) => !!v);
  }, [dashboardViews?.data, defaultView, iMayApplyTemplates, myAros?.data]);

  // Select a valid view. If the current activeViewId (from URL or ref) doesn't
  // match any view, pick the first one. Writing to the ref gives us the correct
  // value immediately in this render; the nuqs setter updates the URL async.
  const tentativeId = nuqsActiveViewId ?? activeViewIdRef.current;
  const hasValidSelection =
    !isLoadingDashboardViews &&
    calculatedDashboardViews.length > 0 &&
    calculatedDashboardViews.some((v) => v.id === tentativeId);

  if (!isLoadingDashboardViews && calculatedDashboardViews.length > 0 && !hasValidSelection) {
    // Only update the ref — URL writes during render interfere with
    // Next.js navigation. The init flow writes to URL when ready.
    activeViewIdRef.current = calculatedDashboardViews[0].id;
  }

  // Derive activeViewId AFTER default selection so ref has the correct value
  const activeViewId = nuqsActiveViewId ?? activeViewIdRef.current;

  // Pure lookup — no fallback. The logic above guarantees activeViewId is valid.
  const activeView = useMemo(() => {
    if (isLoadingDashboardViews) return undefined;
    return calculatedDashboardViews.find((x) => x.id === activeViewId);
  }, [calculatedDashboardViews, activeViewId, isLoadingDashboardViews]);

  return {
    dashboardViews: calculatedDashboardViews,
    isLoadingDashboardViews,
    createDashboardView: createMutation,
    createDashboardViewAsync,
    updateDashboardView: updateMutation,
    deleteDashboardView: deleteMutation,
    activeViewId,
    setActiveViewId,
    activeView,
    defaultView,
  };
};
